import { useReaderMode } from '../../hooks/useReaderMode';
import { markDiscovered } from '../../state/coachStore';

interface Props {
  /** The id of the surface this toggle controls. Must match the
   *  `<ReaderSurface id="...">` wrapping the readable content. */
  surfaceId: string;
  /** Optional class for layout customization on the host surface. */
  className?: string;
  /** When true, marks this toggle as the canonical `[data-coach-id=reader]` anchor for
   *  the reader-mode coach (coach-tooltip-system Phase 3a). Set on exactly ONE instance
   *  (the chat-view toggle) so the popover has a single, unambiguous target. */
  coachAnchor?: boolean;
}

/**
 * Small button that enters/exits Reader Mode for a specific surface.
 * Visual state reflects the global reader state — only one surface can be
 * active at a time.
 */
export function ReaderToggle({ surfaceId, className, coachAnchor }: Props) {
  const reader = useReaderMode();
  const isMine = reader.active && reader.activeSurfaceId === surfaceId;

  const cls = `reader-toggle ${isMine ? 'active' : ''} ${className || ''}`.trim();

  return (
    <button
      type="button"
      class={cls}
      {...(coachAnchor ? { 'data-coach-id': 'reader' } : {})}
      onClick={() => { reader.toggle(surfaceId); markDiscovered('reader-mode'); }}
      aria-pressed={isMine}
      aria-label={isMine ? 'Exit Reader Mode' : 'Enter Reader Mode'}
      title={isMine ? 'Exit Reader Mode' : 'Reader Mode'}
    >
      <span aria-hidden="true">📖</span>
    </button>
  );
}
